<template>
    <DataTable
        v-bind="primeVueAttrs"
        ref="table"
        class="e-table"
        removable-sort
        table-style="min-width: 100%"
        :row-style="() => ({ height: '4rem' })"
        :row-class="resolveRowClass"
        :selection="selectionValue"
        :selection-mode="selectionMode"
        :expanded-rows="detailed ? internalExpandedRows : null"
        :data-key="detailKey"
        :meta-key-selection="false"
        :sort-field="defaultSort[0]"
        :sort-order="defaultSort[1] === 'asc' ? 1 : -1"
        @page="onPrimeVuePage"
        @sort="handlePrimeVueSort"
        @update:selection="handleSelectionChange"
        @update:expanded-rows="handleExpandedRowsChange"
        @row-select="handleRowSelect"
        @row-unselect="handleRowUnselect"
        @row-select-all="handleRowSelectAll"
        @row-unselect-all="handleRowUnselectAll"
        @row-click="handleRowClick"
    >
        <component
            :is="columnVNode"
            v-for="(columnVNode, index) in columnVNodes"
            :key="index"
        />

        <template v-if="$slots.footer" #footer="scope">
            <slot name="footer" v-bind="scope && scope.key !== undefined ? scope : {}" />
        </template>

        <template v-if="$slots.empty" #empty="scope">
            <slot name="empty" v-bind="scope && scope.key !== undefined ? scope : {}" />
        </template>

        <template v-if="$slots.loading" #loading="scope">
            <slot name="loading" v-bind="scope && scope.key !== undefined ? scope : {}" />
        </template>

        <!-- Map Buefy's #detail slot to PrimeVue's #expansion slot -->
        <template v-if="detailed && $slots.detail" #expansion="slotProps">
            <table v-if="isCustomDetailRow" class="e-table-expansion-table">
                <tbody>
                    <slot name="detail" :row="slotProps.data" :index="slotProps.index" />
                </tbody>
            </table>
            <slot v-else name="detail" :row="slotProps.data" :index="slotProps.index" />
        </template>

        <template v-if="isPaginated" #paginatorcontainer>
            <EdverumTableOrmPaginator
                v-if="isEdverumTableOrmPaginator"
                :current-page="internalCurrentPage"
                :total-pages="totalPages"
                :per-page="primeVueAttrs.rows || 25"
                :total-records="paginationTotalRecords"
                :disclaimer-text="disclaimerText"
                @page-change="handleCustomPageChange"
                @per-page-change="handlePerPageChange"
            />

            <slot v-else-if="$slots.pagination" name="pagination" />

            <EPagination
                v-else
                :total="paginationTotalRecords"
                :current="internalCurrentPage"
                :per-page="$attrs['per-page'] || $attrs.perPage || 25"
                @change="handleCustomPageChange"
            />
        </template>
    </DataTable>
</template>

<script>
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { computed, getCurrentInstance, h } from 'vue';
import EdverumTableOrmPaginator from './EdverumTableOrmPaginator.vue';
import EPagination from './EPagination.vue';
import ETableSelectAllHeader from './ETableSelectAllHeader.vue';

/**
 * ETable - Abstraction wrapper around Buefy's b-table and PrimeVue's DataTable
 *
 * Usage:
 *   <e-table :data="users" :columns="columns">
 *     <e-table-column field="name" label="Name" sortable />
 *     <e-table-column field="email" label="Email" />
 *     <e-table-column v-slot="props" field="status" label="Status">
 *       <e-tag :type="props.row.status">{{ props.row.status }}</e-tag>
 *     </e-table-column>
 *   </e-table>
 *
 *   <e-table :data="data" paginated per-page="10" striped hoverable>
 *     <e-table-column field="id" label="ID" numeric sortable />
 *     <e-table-column field="title" label="Title" sortable />
 *   </e-table>
 *
 * All Buefy b-table props, events, and slots are supported.
 *
 *
 * Buefy to PrimeVue prop mappings:
 *   - data -> value
 *   - paginated -> paginator
 *   - per-page -> :rows
 *   - striped -> stripedRows
 *   - hoverable -> (default in PrimeVue)
 *   - checkable -> adds Column with selectionMode="multiple"
 *   - checked-rows / v-model:checked-rows -> v-model:selection
 *   - custom-row-key -> dataKey (defaults to 'id' when checkable is enabled)
 *   - detailed -> enables row expansion
 *   - detail-key -> dataKey for identifying expanded rows
 *   - show-detail-icon -> adds expander column (default: true)
 *   - #detail slot -> #expansion slot (row prop mapped from data)
 *   - selected / v-model:selected -> selectionMode="single" + selection
 *   - @select event -> emitted when a row is clicked/selected
 *
 * Architecture (PrimeVue mode):
 *   - Uses Vue's h() function to render DataTable with Column children
 *   - ETableColumn acts as a configuration holder (renders b-table-column for Buefy)
 *   - ETable finds ETableColumn VNodes in slots and creates Column VNodes from their props/slots
 *   - Column VNodes are created directly using h() and passed as DataTable children
 *
 * To access underlying table methods, expose specific methods here as needed.
 */
export default {
    name: 'ETable',
    components: {
        DataTable,
        EdverumTableOrmPaginator,
        EPagination,
    },
    inheritAttrs: false,
    props: {
        paginatorType: {
            type: String,
            default: 'default',
            validator: value => ['default', 'edverum-table', 'edverum-table-orm'].includes(value),
        },
        disclaimerText: {
            type: String,
            default: '',
        },
        checkable: {
            type: Boolean,
            default: false,
        },
        checkedRows: {
            type: Array,
            default: () => [],
        },
        headerCheckable: {
            type: Boolean,
            default: true,
        },
        headerCheckboxState: {
            type: String,
            default: null,
            validator: value => value === null || ['none', 'some', 'all'].includes(value),
        },
        headerCheckboxDisabled: {
            type: Boolean,
            default: false,
        },
        headerCheckboxDisabledTooltip: {
            type: String,
            default: '',
        },
        // Row expansion props (Buefy compatibility)
        detailed: {
            type: Boolean,
            default: false,
        },
        detailKey: {
            type: String,
            default: 'id',
        },
        showDetailIcon: {
            type: Boolean,
            default: true,
        },
        // Row selection props (Buefy compatibility)
        selected: {
            type: Object,
            default: null,
        },
        focusable: {
            type: Boolean,
            default: false,
        },
        defaultSort: {
            type: Array,
            default: () => [],
        },
    },
    emits: [
        'update:checkedRows',
        'check',
        'page-change',
        'per-page-change',
        'sort',
        'details-open',
        'details-close',
        'search',
        'update:selected',
        'select',
        'row-click',
    ],
    setup() {
        const instance = getCurrentInstance();

        // Check if 'selected' prop was explicitly passed by parent (exists in vnode.props)
        const hasSelectedBinding = computed(() => {
            return instance?.vnode?.props && 'selected' in instance.vnode.props;
        });

        return {
            hasSelectedBinding,
        };
    },
    data() {
        return {
            internalCurrentPage: 1,
            internalExpandedRows: {}, // For PrimeVue row expansion
            internalSelectedRow: null, // For PrimeVue row selection
            searchDebounceTimer: null,
        };
    },
    computed: {
        $el() {
            return this.$refs.table?.$el;
        },
        isEdverumTableOrmPaginator() {
            return this.paginatorType === 'edverum-table-orm';
        },
        isPaginated() {
            return this.$attrs.paginated !== undefined;
        },
        // Determine selection mode based on props
        // Enable single selection if :selected prop was bound by parent
        selectionMode() {
            // Check if parent passed :selected prop (detected via vnode.props in setup)
            if (this.hasSelectedBinding) {
                return 'single';
            }
            return null;
        },
        // Compute selection value based on mode
        selectionValue() {
            if (this.checkable) {
                return this.checkedRows;
            }
            if (this.selectionMode === 'single') {
                // Use internal state which syncs with the selected prop
                return this.internalSelectedRow || null;
            }
            return null;
        },
        // Property for accessing expanded row keys
        visibleDetailRows() {
            return this.getExpandedRowKeys(this.internalExpandedRows).map(key => {
                const row = this.findRowByDetailKey(key);

                return row ? row[this.detailKey] : key;
            });
        },
        totalPages() {
            const total = this.paginationTotalRecords;
            const perPage = this.$attrs.perPage || this.$attrs['per-page'] || this.$attrs.rows || 25;
            return Math.ceil(total / perPage);
        },
        paginationTotalRecords() {
            if (this.$attrs.total !== undefined) {
                return this.$attrs.total;
            }

            if (this.$attrs.totalRecords !== undefined) {
                return this.$attrs.totalRecords;
            }

            if (!this.isPaginated) {
                return 0;
            }

            const data = this.$attrs.data ?? this.$attrs.value;
            return Array.isArray(data) ? data.length : 0;
        },
        externalCurrentPage() {
            return this.$attrs.currentPage || this.$attrs['current-page'] || 1;
        },
        isCustomDetailRow() {
            return Boolean(this.$attrs.customDetailRow ?? this.$attrs['custom-detail-row']);
        },
        openedDetailedRows() {
            return this.$attrs.openedDetailed ?? this.$attrs['opened-detailed'] ?? [];
        },
        primeVueAttrs() {
            const mapped = { ...this.$attrs };

            if (mapped.data !== undefined) {
                mapped.value = mapped.data;
                delete mapped.data;
            }

            if (mapped.paginated !== undefined) {
                mapped.paginator = true;
                delete mapped.paginated;
            } else {
                mapped.paginator = false;
            }

            if (mapped.perPage !== undefined) {
                mapped.rows = mapped.perPage;
                delete mapped.perPage;
            }

            if (mapped['per-page'] !== undefined) {
                mapped.rows = mapped['per-page'];
                delete mapped['per-page'];
            }

            if (mapped.backendPagination !== undefined) {
                mapped.lazy = true;
                delete mapped.backendPagination;
            }
            if (mapped['backend-pagination'] !== undefined) {
                mapped.lazy = true;
                delete mapped['backend-pagination'];
            }

            if (mapped.total !== undefined) {
                mapped.totalRecords = mapped.total;
                delete mapped.total;
            }

            if (mapped.totalRecords === undefined && mapped.paginator && !mapped.lazy) {
                mapped.totalRecords = this.paginationTotalRecords;
            }

            const currentPage = mapped.currentPage ?? mapped['current-page'] ?? this.internalCurrentPage ?? 1;
            if (mapped.paginator) {
                mapped.first = (currentPage - 1) * (mapped.rows || 25);
            }
            if (mapped.currentPage !== undefined) delete mapped.currentPage;
            if (mapped['current-page'] !== undefined) delete mapped['current-page'];

            if (mapped.striped !== undefined) {
                mapped.stripedRows = true;
                delete mapped.striped;
            }

            if (mapped.hoverable !== undefined) {
                mapped.rowHover = true;
                delete mapped.hoverable;
            }

            // Map Buefy's custom-row-key to PrimeVue's dataKey
            if (mapped.customRowKey !== undefined) {
                mapped.dataKey = mapped.customRowKey;
                delete mapped.customRowKey;
            }
            if (mapped['custom-row-key'] !== undefined) {
                mapped.dataKey = mapped['custom-row-key'];
                delete mapped['custom-row-key'];
            }

            // Default dataKey to 'id' if checkable is enabled and no key specified
            if (this.checkable && !mapped.dataKey) {
                mapped.dataKey = 'id';
            }

            if (mapped.openedDetailed !== undefined) delete mapped.openedDetailed;
            if (mapped['opened-detailed'] !== undefined) delete mapped['opened-detailed'];
            if (mapped.customDetailRow !== undefined) delete mapped.customDetailRow;
            if (mapped['custom-detail-row'] !== undefined) delete mapped['custom-detail-row'];
            if (mapped.backendSorting !== undefined) delete mapped.backendSorting;
            if (mapped['backend-sorting'] !== undefined) delete mapped['backend-sorting'];
            if (mapped.narrowed !== undefined) delete mapped.narrowed;

            // Our template binds :row-class explicitly (resolveRowClass), so strip any
            // passthrough copies to avoid conflicting bindings.
            if (mapped.rowClass !== undefined) delete mapped.rowClass;
            if (mapped['row-class'] !== undefined) delete mapped['row-class'];

            const defaultSortDirection = mapped.defaultSortDirection ?? mapped['default-sort-direction'];
            if (defaultSortDirection !== undefined) {
                mapped.defaultSortOrder = defaultSortDirection === 'desc' ? -1 : 1;
                delete mapped.defaultSortDirection;
                delete mapped['default-sort-direction'];
            }

            // Add PassThrough (pt) prop for styling to override Bulma styles
            mapped.pt = {
                root: {
                    style: 'margin: 0 !important; flex-grow: 0 !important',
                },
                table: {
                    style: 'margin: 0 !important; width: 100% !important',
                },
                column: {
                    bodycell: {
                        style: 'padding: 0.75rem 0.5rem !important; flex-grow: 0 !important',
                    },
                    headercell: {
                        style: 'padding: 0.75rem 0.5rem !important; flex-grow: 0 !important',
                    },
                },
            };

            return mapped;
        },
        columnVNodes() {
            const columns = [];

            // Add expander column if detailed and showDetailIcon are enabled
            if (this.detailed && this.showDetailIcon) {
                columns.push(
                    h(Column, {
                        expander: true,
                        headerStyle: 'width: 3rem',
                        bodyStyle: 'width: 3rem',
                    })
                );
            }

            // Add checkbox column if checkable is enabled
            if (this.checkable) {
                const { headerCheckboxState } = this;

                const checkboxColumnProps = {
                    selectionMode: 'multiple',
                    headerStyle: 'width: 3rem',
                };
                const checkboxColumnSlots = {};

                const useCustomHeaderCheckbox = this.headerCheckable && Boolean(headerCheckboxState);

                if (!this.headerCheckable || useCustomHeaderCheckbox) {
                    checkboxColumnProps.pt = {
                        pcHeaderCheckbox: {
                            root: { style: 'display: none' },
                        },
                    };
                }

                if (useCustomHeaderCheckbox) {
                    const { headerCheckboxDisabled } = this;
                    const disabledTooltip = this.headerCheckboxDisabledTooltip;

                    checkboxColumnSlots.header = () =>
                        h(ETableSelectAllHeader, {
                            state: headerCheckboxState,
                            disabled: headerCheckboxDisabled,
                            tooltip: disabledTooltip,
                            onToggle: () => this.$emit('check', this.checkedRows, null),
                        });
                }

                columns.push(h(Column, checkboxColumnProps, checkboxColumnSlots));
            }

            // Collect Column VNodes that are already rendered inside ETableColumn components
            columns.push(...this.collectColumnVNodes());

            return columns;
        },
    },
    watch: {
        externalCurrentPage: {
            immediate: true,
            handler(newValue) {
                if (newValue && newValue !== this.internalCurrentPage) {
                    this.internalCurrentPage = newValue;
                }
            },
        },
        // Watch for external changes to the selected prop
        selected(newValue) {
            this.internalSelectedRow = newValue || null;
        },
        openedDetailedRows: {
            immediate: true,
            handler(newValue) {
                if (!this.detailed) {
                    return;
                }

                this.internalExpandedRows = this.normalizeOpenedDetailedRows(newValue);
            },
        },
    },
    mounted() {
        // Initialize selected row from prop (handle undefined/null gracefully)
        if (this.selected) {
            this.internalSelectedRow = this.selected;
        }
    },

    beforeUnmount() {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }
    },
    methods: {
        onPrimeVuePage(event) {
            this.internalCurrentPage = event.page + 1;
            this.$emit('page-change', event.page + 1);
        },
        handleCustomPageChange(page) {
            this.internalCurrentPage = page;
            this.$emit('page-change', page);
        },
        handlePerPageChange(perPage) {
            this.$emit('per-page-change', perPage);
        },
        handlePrimeVueSort(event) {
            // PrimeVue emits sort event with { sortField, sortOrder } where sortOrder is 1 (asc) or -1 (desc).
            // PrimeVue's removable single-sort drives the per-column first → opposite → unsorted cycle and
            // resets to the table-wide defaultSortOrder when a different column is clicked, so each column's
            // state is independent. Emit to parent in the same format - EdverumTableOrm handles the conversion.
            this.$emit('sort', event);
        },
        handleSelectionChange(selection) {
            if (this.checkable) {
                // Checkbox mode: emit update:checkedRows for v-model support
                this.$emit('update:checkedRows', selection);
            } else if (this.selectionMode === 'single') {
                // Single row selection mode
                this.internalSelectedRow = selection;
                this.$emit('update:selected', selection);
                this.$emit('select', selection);
            }
        },
        handleRowSelect(event) {
            // Only handle checkbox mode here - single selection is handled by handleSelectionChange
            if (this.checkable) {
                // Checkbox mode: Emit Buefy-compatible @check event with (checkedRows, row) signature
                const newCheckedRows = [...this.checkedRows];
                if (!newCheckedRows.find(r => this.getRowKey(r) === this.getRowKey(event.data))) {
                    newCheckedRows.push(event.data);
                }
                this.$emit('check', newCheckedRows, event.data);
            }
        },
        handleRowUnselect(event) {
            // Only handle checkbox mode here - single selection is handled by handleSelectionChange
            if (this.checkable) {
                // Checkbox mode: Emit Buefy-compatible @check event with (checkedRows, row) signature
                const newCheckedRows = this.checkedRows.filter(r => this.getRowKey(r) !== this.getRowKey(event.data));
                this.$emit('check', newCheckedRows, event.data);
            }
        },
        handleRowSelectAll(event) {
            // Emit Buefy-compatible @check event when all rows are selected
            this.$emit('check', event.data, null);
        },
        handleRowUnselectAll() {
            // Emit Buefy-compatible @check event when all rows are unselected
            this.$emit('check', [], null);
        },
        handleRowClick(event) {
            const originalEvent = event?.originalEvent;
            const clickTarget = originalEvent?.target;

            // Checkbox clicks already toggle via @row-select / @check — avoid double-toggle.
            if (
                clickTarget?.closest?.(
                    '.p-checkbox, .p-datatable-row-editor-init, .p-datatable-row-toggle-button, button, a, input, label'
                )
            ) {
                this.$emit('row-click', event);
                return;
            }

            if (clickTarget?.closest?.('td.p-selection-column, th.p-selection-column')) {
                this.$emit('row-click', event);
                return;
            }

            if (this.checkable && event?.data) {
                this.$emit('select', event.data);
            }

            this.$emit('row-click', event);
        },
        handleExpandedRowsChange(expandedRows) {
            const normalizedExpandedRows = this.normalizeExpandedRows(expandedRows);

            // Track which rows were added/removed for events
            const previousKeys = this.getExpandedRowKeys(this.internalExpandedRows).map(key => String(key));
            const newKeys = this.getExpandedRowKeys(normalizedExpandedRows).map(key => String(key));

            // Find newly expanded rows
            newKeys.forEach(key => {
                if (!previousKeys.includes(key)) {
                    this.$emit('details-open', key);
                }
            });

            // Find collapsed rows
            previousKeys.forEach(key => {
                if (!newKeys.includes(key)) {
                    this.$emit('details-close', this.findRowByDetailKey(key) || key);
                }
            });

            this.internalExpandedRows = normalizedExpandedRows;
        },
        // Method to toggle row expansion
        toggleDetails(row) {
            const rowKey = row[this.detailKey];

            if (rowKey === undefined || rowKey === null) {
                return;
            }

            const newExpandedRows = this.normalizeExpandedRows(this.internalExpandedRows);

            if (newExpandedRows[rowKey] !== undefined) {
                delete newExpandedRows[rowKey];
            } else {
                newExpandedRows[rowKey] = true;
            }

            this.handleExpandedRowsChange(newExpandedRows);
        },
        normalizeExpandedRows(expandedRows) {
            if (!expandedRows) {
                return {};
            }

            if (Array.isArray(expandedRows)) {
                return expandedRows.reduce((acc, row) => {
                    const key = row?.[this.detailKey];

                    if (key !== undefined && key !== null) {
                        acc[key] = true;
                    }

                    return acc;
                }, {});
            }

            return Object.keys(expandedRows).reduce((acc, key) => {
                acc[key] = true;

                return acc;
            }, {});
        },
        getExpandedRowKeys(expandedRows) {
            if (!expandedRows) {
                return [];
            }

            if (Array.isArray(expandedRows)) {
                return expandedRows.map(row => row?.[this.detailKey]).filter(key => key !== undefined && key !== null);
            }

            return Object.keys(expandedRows);
        },
        findRowByDetailKey(key) {
            const tableData = this.primeVueAttrs.value || [];

            return tableData.find(row => String(row[this.detailKey]) === String(key));
        },
        normalizeOpenedDetailedRows(openedDetailedRows) {
            if (!Array.isArray(openedDetailedRows) || openedDetailedRows.length === 0) {
                return {};
            }

            return openedDetailedRows.reduce((acc, item) => {
                const key = item !== null && typeof item === 'object' ? item[this.detailKey] : item;

                if (key !== undefined && key !== null) {
                    acc[key] = true;
                }

                return acc;
            }, {});
        },
        getRowKey(row) {
            const dataKey = this.primeVueAttrs.dataKey || 'id';
            return row ? row[dataKey] : null;
        },
        resolveRowClass(rowData) {
            const incoming = this.$attrs.rowClass ?? this.$attrs['row-class'];
            const baseClass = typeof incoming === 'function' ? incoming(rowData) || '' : incoming || '';
            const isRowCheckable = this.$attrs.isRowCheckable ?? this.$attrs['is-row-checkable'];

            if (this.checkable && typeof isRowCheckable === 'function' && !isRowCheckable(rowData)) {
                return `${baseClass} e-table-uncheckable-row`.trim();
            }

            return baseClass;
        },
        collectColumnVNodes() {
            const columnVNodes = [];
            const nonColumnSlots = ['footer', 'empty', 'pagination', 'loading', 'detail'];

            // Process all slots to find ETableColumn components and extract their Column children
            Object.keys(this.$slots)
                .filter(slotName => !nonColumnSlots.includes(slotName))
                .forEach(slotName => {
                    const slotContent = this.$slots[slotName]();
                    if (!slotContent) {
                        return;
                    }
                    this.extractColumnVNodes(slotContent, columnVNodes);
                });

            return columnVNodes;
        },
        extractColumnVNodes(vnodes, columnVNodes) {
            if (!vnodes || !Array.isArray(vnodes)) {
                return;
            }

            vnodes.forEach(vnode => {
                if (this.isETableColumn(vnode)) {
                    // Create a Column VNode using the same mapping logic as ETableColumn
                    const columnVNode = this.createColumnFromETableColumn(vnode);
                    if (columnVNode) {
                        columnVNodes.push(columnVNode);
                    }
                } else if (vnode.children) {
                    // Recursively process children to find nested ETableColumns
                    if (Array.isArray(vnode.children)) {
                        this.extractColumnVNodes(vnode.children, columnVNodes);
                    } else if (typeof vnode.children === 'object') {
                        Object.values(vnode.children).forEach(childSlot => {
                            if (typeof childSlot === 'function') {
                                this.extractColumnVNodes(childSlot(), columnVNodes);
                            } else if (Array.isArray(childSlot)) {
                                this.extractColumnVNodes(childSlot, columnVNodes);
                            }
                        });
                    }
                }
            });
        },
        isETableColumn(vnode) {
            const componentName = vnode.type?.name;
            return componentName === 'ETableColumn';
        },
        createColumnFromETableColumn(etableColumnVNode) {
            // Extract props from ETableColumn and map them using the same logic as ETableColumn
            const props = etableColumnVNode.props || {};
            const mappedProps = this.mapBuefyToPrimeVueColumnProps(props);

            // Build slots for the Column component
            const columnSlots = {};

            // Handle slot content from ETableColumn
            if (etableColumnVNode.children && typeof etableColumnVNode.children === 'object') {
                Object.keys(etableColumnVNode.children).forEach(slotName => {
                    const slotFn = etableColumnVNode.children[slotName];

                    if (slotName === 'default') {
                        // Map default slot to body slot for PrimeVue Column
                        columnSlots.body = slotProps => {
                            // Map PrimeVue slot props to Buefy format (same as ETableColumn does)
                            const mappedSlotProps = this.mapPrimeVueSlotProps(slotProps);
                            return slotFn(mappedSlotProps);
                        };
                    } else {
                        // Pass through other slots
                        columnSlots[slotName] = slotFn;
                    }
                });
            }

            // Create and return a Column VNode using h()
            return h(Column, mappedProps, columnSlots);
        },
        mapBuefyToPrimeVueColumnProps(props) {
            const mapped = { ...props };

            // Map Buefy props to PrimeVue props (same logic as ETableColumn)
            if (mapped.label !== undefined) {
                mapped.header = mapped.label;
                delete mapped.label;
            }

            if (mapped.numeric !== undefined) {
                mapped.dataType = 'numeric';
                delete mapped.numeric;
            }

            return mapped;
        },
        mapPrimeVueSlotProps(scope) {
            return {
                ...scope,
                row: scope?.data, // PrimeVue uses 'data', Buefy uses 'row'
                index: scope?.index,
            };
        },
        initSort() {
            this.$refs.table?.initSort?.();
        },
        resetMultiSorting() {
            this.$refs.table?.resetMultiSorting?.();
        },
        focus() {
            this.$refs.table?.focus?.();
        },

        debouncedSearch(query) {
            // Clear existing timer
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }

            const q = query ?? '';

            // If empty, emit immediately to reset filters
            if (!q || String(q).trim() === '') {
                this.$emit('search', q);
                return;
            }

            // Set new timer for 500ms delay
            this.searchDebounceTimer = setTimeout(() => {
                this.$emit('search', q);
            }, 500);
        },
    },
};
</script>

<style lang="scss">
table {
    color: var(--p-text-color);
    th {
        color: var(--p-text-color);
    }
}

// Keep the DataTable's own loading backdrop subtle. Scoped to the table mask
// so it no longer leaks into dialog/drawer overlays app-wide (ClickUp 868jgyc8c).
.p-datatable-mask.p-overlay-mask {
    background-color: rgba(0, 0, 0, 0.1) !important;
}

.e-table-expansion-table {
    width: 100%;
    margin: 0;
    border-collapse: collapse;
}

// Legacy Bulma `table td { vertical-align: top }` styling leaks onto PrimeVue's
// DataTable markup, misaligning cell content (e.g. checkboxes) against
// vertically-centered content like UserMenu in a sibling column. Scoped to
// .e-table so this doesn't restyle every PrimeVue DataTable in the app,
// including ones that render <primevue/datatable> directly (not via ETable).
.e-table .p-datatable-tbody > tr > td {
    vertical-align: middle;
}

// The checkbox's own inline-block box doesn't center reliably via vertical-align.
// Center it inside its own cell instead of touching the <td>'s display (overriding
// the <td> itself breaks its stretch-to-row-height table layout behavior).
.e-table td[data-p-selection-column='true'] .p-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

// Rows whose selection is not currently allowed (e.g. a selection limit was reached):
// disable the checkbox so the user cannot select beyond the cap, while still allowing
// already-selected rows to be unchecked.
tr.e-table-uncheckable-row .p-checkbox {
    opacity: 0.5;
    pointer-events: none;
}

</style>